import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useCustomMutation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Label, Send } from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { WarehouseMessage } from "../../orders/utils/types";

interface WarehouseMessagesProps {
  warehouseMessages: WarehouseMessage[];
}

const MAX_LENGTH = 2000;

/** 🔥 TEK KAYNAK */
const PRESETS = [
  {
    key: "dontSend",
    labelKey: "dontSendToCustomer",
    value: "Don't send to customer",
    color: "error" as const,
    border: "error.main",
  },
  {
    key: "canSend",
    label: "Now can send to customer",
    value: "Now can send to customer",
    color: "success" as const,
    border: "success.main",
  },
];

export const WarehouseMessages = ({
  warehouseMessages,
}: WarehouseMessagesProps) => {
  const { orderId } = useParams<{ orderId: string }>();
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const { translate } = useTranslation("pages.orders.detail.notes");
  const queryClient = useQueryClient();

  const [input, setInput] = useState("");
  const [quickMessageAnchor, setQuickMessageAnchor] =
    useState<HTMLElement | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const { mutate, isLoading } = useCustomMutation();

  const trimmedInput = input.trim();
  const trimmedLength = trimmedInput.length;
  const isTooLong = trimmedLength > MAX_LENGTH;

  const selectedPreset = PRESETS.find((p) => p.value === trimmedInput);

  const sortedMessages = useMemo(
    () =>
      [...warehouseMessages].sort((a, b) => a.created.localeCompare(b.created)),
    [warehouseMessages],
  );

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [sortedMessages]);

  const handleQuickMessageMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setQuickMessageAnchor(event.currentTarget);
  };

  const handleQuickMessageSelect = (value: string) => {
    setInput(value);
    setQuickMessageAnchor(null);
  };

  const handleSend = () => {
    if (
      !trimmedInput ||
      trimmedLength > MAX_LENGTH ||
      !selectedCountry ||
      isLoading
    )
      return;

    mutate(
      {
        url: `store/${selectedCountry.id}/order/${orderId}/warehouse-messages`,
        method: "post",
        values: { message: trimmedInput },
        successNotification: () => ({
          message: translate("sendSuccess"),
          type: "success",
        }),
      },
      {
        onSuccess: async () => {
          const queryKey = [
            "order-detail",
            selectedCountry.id,
            String(orderId),
          ];

          setInput("");
          setQuickMessageAnchor(null);

          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });
        },
      },
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="subtitle1" fontWeight={500}>
        {translate("warehouseChat")}
      </Typography>

      <Divider sx={{ my: 1 }} />

      {/* MESSAGES */}
      {sortedMessages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate("warehouseEmpty")}
        </Typography>
      ) : (
        <Stack
          ref={messagesContainerRef}
          spacing={1.5}
          sx={{ maxHeight: 360, overflowY: "auto", flex: 1 }}
        >
          {sortedMessages.map((message) => {
            const isSyncrosale = message.direction === "SYNCROSALE";

            const preset = PRESETS.find((p) => p.value === message.message);

            let borderColor: string;
            if (preset) {
              borderColor = preset.border;
            } else if (isSyncrosale) {
              borderColor = "divider";
            } else {
              borderColor = "primary.main";
            }

            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: isSyncrosale ? "flex-end" : "flex-start",
                  maxWidth: "82%",
                  p: 1.25,
                  borderRadius: 2,
                  borderTopLeftRadius: isSyncrosale ? 16 : 0,
                  borderTopRightRadius: isSyncrosale ? 0 : 16,
                  border: preset ? "2px solid" : "1px solid",
                  borderColor,
                }}
              >
                <Stack direction="row" spacing={1} mb={0.5}>
                  <Chip
                    size="small"
                    color={isSyncrosale ? "default" : "primary"}
                    label={
                      isSyncrosale
                        ? message.author
                        : translate("warehouse", {
                            defaultValue: message.direction,
                          })
                    }
                    sx={{ height: 20 }}
                  />

                  <Typography variant="caption" color="text.secondary">
                    {new Date(message.created).toLocaleString()}
                  </Typography>
                </Stack>

                <Typography variant="body2">{message.message}</Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* INPUT */}
      <Stack direction="row" spacing={1} alignItems={"flex-start"}>
        <IconButton
          color={selectedPreset?.color || "default"}
          onClick={handleQuickMessageMenuOpen}
        >
          <Label fontSize="small" />
        </IconButton>

        <TextField
          fullWidth
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={translate("typeWarehouseMessage")}
          error={isTooLong}
          helperText={`${trimmedLength}/${MAX_LENGTH}`}
          FormHelperTextProps={{ sx: { textAlign: "right" } }}
        />

        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={
            isLoading || trimmedLength === 0 || trimmedLength > MAX_LENGTH
          }
        >
          <Send fontSize="small" />
        </IconButton>

        {/* MENU */}
        <Menu
          anchorEl={quickMessageAnchor}
          open={Boolean(quickMessageAnchor)}
          onClose={() => setQuickMessageAnchor(null)}
        >
          <Stack spacing={1} sx={{ p: 1.5, minWidth: 220 }}>
            {PRESETS.map((preset) => {
              const label = preset.labelKey
                ? translate(preset.labelKey)
                : preset.label;

              const selected = trimmedInput === preset.value;

              return (
                <Chip
                  key={preset.key}
                  size="small"
                  label={label}
                  clickable
                  color={preset.color}
                  variant={selected ? "filled" : "outlined"}
                  onClick={() => handleQuickMessageSelect(preset.value)}
                />
              );
            })}
          </Stack>
        </Menu>
      </Stack>
    </Paper>
  );
};
